<template>
  <b-overlay
    :show="loading"
    spinner-variant="primary"
    spinner-type="grow"
    spinner-small
    rounded="sm"
  >
    <b-card>
      <validation-observer
        ref="formUserEditRef"
        #default="{ handleSubmit }"
      >
        <b-form
          class="auth-login-form mt-2"
          @submit.prevent="handleSubmit(saveUserForm)"
        >
          <!-- Media -->
          <b-media class="mb-2">
            <template #aside>
              <b-avatar
                ref="previewEl"
                :src="userDataLocal.avatar"
                :variant="`light-${resolveUserRoleVariant(userDataLocal.role)}`"
                size="90px"
                rounded
              />
            </template>
            <h4 class="mb-1">
              {{ userDataLocal.full_name }} ({{ userDataLocal.username }})
            </h4>
            <div class="d-flex flex-wrap">
              <b-button
                variant="primary"
                @click="$refs.refInputEl.click()"
              >
                <input
                  ref="refInputEl"
                  type="file"
                  class="d-none"
                  @input="inputImageRenderer"
                >
                <span class="d-none d-sm-inline">{{ $t('Upload') }}</span>
                <feather-icon
                  icon="EditIcon"
                  class="d-inline d-sm-none"
                />
              </b-button>
              <b-button
                variant="outline-secondary"
                class="ml-1"
                @click="removeAvatar"
              >
                <span class="d-none d-sm-inline"> {{ $t('Remove') }}</span>
                <feather-icon
                  icon="TrashIcon"
                  class="d-inline d-sm-none"
                />
              </b-button>
            </div>
          </b-media>

          <!-- User Info: Input Fields -->

          <b-row>

            <!-- Field: Full Name -->
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="full_name"
                rules="required|max:30"
              >
                <b-form-group
                  :label="$t('Full Name')"
                  label-for="full-name"
                >
                  <template #label>
                    <label class="d-block font-weight-bolder">{{ $t('Full Name') }} <span class="text-danger">*</span></label>
                  </template>

                  <b-form-input
                    id="full-name"
                    v-model="userDataLocal.full_name"
                    name="full_name"
                    :state="errors.length > 0 ? false : null"
                    placeholder=""
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Field: Phone Number -->
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Phone')"
                rules="max:12"
              >
                <b-form-group
                  :label="$t('Phone')"
                  label-for="phone"
                >
                  <template #label>
                    <label class="d-block font-weight-bolder">{{ $t('Phone') }} </label>
                  </template>
                  <b-form-input
                    id="phone"
                    v-model="userDataLocal.phone"
                    v-mask="{ mask: '0999999999[9]', greedy: true }"
                    :state="errors.length > 0 ? false : null"
                    type="number"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Field: Email -->
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="email"
                rules="required|max:150"
              >
                <b-form-group
                  label="Email"
                  label-for="email"
                >
                  <template #label>
                    <label class="d-block font-weight-bolder">{{ $t('Email') }} <span class="text-danger">*</span></label>
                  </template>
                  <b-form-input
                    id="email"
                    v-model="userDataLocal.email"
                    v-mask="{ alias: 'email' }"
                    :state="errors.length > 0 ? false : null"
                    type="email"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Field: Ngày sinh -->
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="email"
                rules="max:10"
              >
                <b-form-group
                  :label="$t('Birthday')"
                  label-for="birthday"
                >
                  <template #label>
                    <label class="d-block font-weight-bolder">{{ $t('Birthday') }} </label>
                  </template>
                  <b-input-group class="input-group-merge">
                    <b-form-input
                      id="birthday"
                      v-model="userDataLocal.birthday"
                      v-mask="{ mask: '99/99/9999', greedy: true }"
                      :state="errors.length > 0 ? false : null"
                      trim
                    />
                    <b-input-group-append is-text>
                      <feather-icon icon="CalendarIcon" />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

            </b-col>

            <!-- Field: GENDER -->
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group label-for="gender">
                <template #label>
                  <label class="d-block font-weight-bolder">{{ $t('Gender') }} </label>
                </template>
                <v-select
                  v-model="userDataLocal.gender"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="genderOptions"
                  :reduce="(val) => val.id"
                  input-id="gender"
                />
              </b-form-group>
            </b-col>

            <!-- Field: Tác giả -->
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="alias"
                rules="max:100"
              >
                <b-form-group label-for="company">
                  <template #label>
                    <label class="d-block font-weight-bolder">{{ $t('Alias') }} </label>
                  </template>
                  <b-form-input
                    id="alias"
                    v-model="userDataLocal.alias"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Field: Timezone -->
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Time zone')"
                rules="required"
              >
                <b-form-group :state="errors.length > 0 ? false : null">
                  <template #label>
                    <label class="d-block font-weight-bolder">{{ $t('Time zone') }} <span class="text-danger">*</span></label>
                  </template>
                  <v-select
                    v-model="userData.timezone"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="timezones"
                    :reduce="(val) => val.value"
                    label="label"
                    input-id="user-timezone"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Field: Phòng ban -->
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                :label="$t('Department')"
                label-for="user-department"
              >
                {{ userDataLocal.department ? userDataLocal.department.name : '' }}
              </b-form-group>
            </b-col>
            <!-- Field: Phòng ban -->
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                :label="$t('Position')"
                label-for="user-position"
              >
                {{ userDataLocal.position ? userDataLocal.position.name : '' }}
              </b-form-group>
            </b-col>

            <!-- Field: Status -->
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                :label="$t('Status')"
                label-for="user-status"
              >

                <b-badge
                  pill
                  :variant="`light-${resolveUserStatusVariant(userDataLocal.status)}`"
                >
                  {{ resolveUserStatusText(userDataLocal.status) }}
                </b-badge>
              </b-form-group>
            </b-col>

            <!-- Field: Limit Storage -->
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group
                label="Giới hạn dung lượng lưu trữ file  (0: không giới hạn)"
                label-for="user-quote-file"
              >
                {{ userDataLocal.file_storage_quota }}MB
              </b-form-group>
            </b-col>
          </b-row>
          <!-- Action Buttons -->
          <b-button
            type="submit"
            variant="primary"
            class="mb-1 mb-sm-0 mr-0 mr-sm-1"
            :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            :disabled="loading"
          >
            {{ $t('Save Changes') }}
          </b-button>
        </b-form>
      </validation-observer>
    </b-card>
  </b-overlay>
</template>

<script>
import {
  BOverlay,
  BButton,
  BMedia,
  BAvatar,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BForm,
  BCard,
  BBadge,
  BInputGroup,
  BInputGroupAppend,
} from 'bootstrap-vue';
import { avatarText } from '@core/utils/filter';
import vSelect from 'vue-select';
import { useInputImageRenderer } from '@core/comp-functions/forms/form-utils';
import { ref, reactive } from '@vue/composition-api';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
// import flatPickr from 'vue-flatpickr-component'
import {
  required, min, max,
} from '@validations';
import { showToast } from '@core/utils/utils';
import useJwt from '@/auth/jwt/useJwt';
import store from '@/store';
import { timezones } from '@/libs/constants';
import useAccount from './useAccount';

export default {
  components: {
    BOverlay,
    BButton,
    BMedia,
    BAvatar,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BBadge,
    BCard,
    BInputGroup,
    BInputGroupAppend,
    // flatPickr,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  props: ['userData'],
  data() {
    return {
      required,
      min,
      max,
      timezones,
    }
  },
  setup(props) {
    const {
      statusOptions,
      genderOptions,
      resolveUserRoleVariant,
      resolveUserStatusVariant,
      resolveUserStatusText,
    } = useAccount();

    const formUserEditRef = ref(null);
    const refInputEl = ref(null);
    const previewEl = ref(null);
    const selectedGender = ref(2);
    const selectedStatus = ref(0);
    const positionsData = ref([]);
    const departmentsData = ref([]);
    const loading = ref(false);

    const userDataLocal = reactive(JSON.parse(JSON.stringify(props.userData)));

    const saveUserForm = () => {
      formUserEditRef.value.validate().then((success) => {
        if (success) {
          loading.value = true;
          store.dispatch('account/updateAccount', {
            full_name: userDataLocal.full_name,
            email: userDataLocal.email,
            phone: userDataLocal.phone,
            gender: userDataLocal.gender,
            birthday: userDataLocal.birthday,
            alias: userDataLocal.alias,
            timezone: userDataLocal.timezone,
            update_type: 'info',
          })
            .then((res) => {
              loading.value = false;
              if (res.status === 200) {
                showToast(res.data.message, { variant: 'success' })
                useJwt.setUser(res.data.data)
              } else {
                showToast(res.data.message)
              }
            })
        }
      });
    }

    const uploadAvatar = () => {
      // return console.log(userDataLocal.file.files);
      loading.value = true;
      if (userDataLocal.file.files.length) {
        // console.log('fileData', fileData);
        const formData = new FormData();
        formData.append('file', userDataLocal.file.files[0]);
        formData.append('update_type', 'upload_avatar');
        store.dispatch('account/uploadAvatar', formData)
          .then((res) => {
            loading.value = false;
            if (res.status === 200) {
              const user = useJwt.getUser();
              user.avatar = res.data.data.avatar;

              showToast(res.data.message, { variant: 'success' })
              // useJwt.setUser(user)
              userDataLocal.avatar = `${res.data.data.avatar}?t=${(new Date()).getTime()}`;
            } else {
              showToast(res.data.message)
            }
          })
      }
    }

    const removeAvatar = () => {
      // return console.log(userDataLocal.file.files);
      loading.value = true;
      const formData = new FormData();
      formData.append('update_type', 'remove_avatar');
      store.dispatch('account/removeAvatar', formData)
        .then((res) => {
          loading.value = false;
          if (res.status === 200) {
            const user = useJwt.getUser();
            user.avatar = res.data.data.avatar;

            showToast(res.data.message, { variant: 'success' })
            // useJwt.setUser(user)
            userDataLocal.avatar = `${res.data.data.avatar}?t=${(new Date()).getTime()}`;
          } else {
            showToast(res.data.message)
          }
        })
    }

    const { inputImageRenderer } = useInputImageRenderer(
      refInputEl,
      (base64) => {
        // eslint-disable-next-line no-param-reassign
        const maxSize = 1 * 1024 * 1024;
        const validImageTypes = ['image/gif', 'image/jpeg', 'image/png'];
        const fileType = refInputEl.value.files[0].type;

        if (!validImageTypes.includes(fileType)) {
          return showToast('File không đúng định dạng (chỉ cho phép ảnh gif/jpeg/png)');
        }
        if (refInputEl.value.files[0].size > maxSize) {
          return showToast(`File vượt quá dung lượng (> ${maxSize}MB)`);
        }
        userDataLocal.avatar = base64;
        userDataLocal.file = refInputEl.value
        return uploadAvatar();
      },
    );

    return {
      loading,
      userDataLocal,
      saveUserForm,
      uploadAvatar,
      removeAvatar,
      resolveUserRoleVariant,
      resolveUserStatusVariant,
      resolveUserStatusText,
      avatarText,
      statusOptions,
      genderOptions,
      departmentsData,
      positionsData,
      refInputEl,
      previewEl,
      formUserEditRef,
      inputImageRenderer,
      selectedGender,
      selectedStatus,
    };
  },

};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
